import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

export type FontSize = 'normal' | 'large' | 'xlarge';

interface AccessibilitySettings {
  fontSize: FontSize;
  highContrast: boolean;
}

interface AccessibilityContextType extends AccessibilitySettings {
  setFontSize: (size: FontSize) => void;
  setHighContrast: (enabled: boolean) => void;
  fontSizeMultiplier: number;
}

const STORAGE_KEY = 'mingyin-accessibility';

const fontSizeMultipliers: Record<FontSize, number> = {
  normal: 1,
  large: 1.25,
  xlarge: 1.5,
};

const defaultSettings: AccessibilitySettings = {
  fontSize: 'normal',
  highContrast: false,
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...defaultSettings, ...JSON.parse(stored) };
      }
    } catch (e) {
      console.error('Failed to load accessibility settings:', e);
    }
    return defaultSettings;
  });

  // Persist settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to save accessibility settings:', e);
    }
  }, [settings]);

  const setFontSize = (fontSize: FontSize) => {
    setSettings((prev) => ({ ...prev, fontSize }));
  };

  const setHighContrast = (highContrast: boolean) => {
    setSettings((prev) => ({ ...prev, highContrast }));
  };

  const fontSizeMultiplier = useMemo(() => fontSizeMultipliers[settings.fontSize], [settings.fontSize]);

  const value = useMemo(
    () => ({
      ...settings,
      setFontSize,
      setHighContrast,
      fontSizeMultiplier,
    }),
    [settings, fontSizeMultiplier]
  );

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = (): AccessibilityContextType => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export { fontSizeMultipliers };
